import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, ArrowRight, Check, Building2, Users, FileText, 
  Layers, Database, Zap, Send, Calculator, Crown, Shield,
  Briefcase, Target, BarChart3, Settings, Mail
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { useCurrency } from '../../contexts/CurrencyContext';

const API = process.env.REACT_APP_BACKEND_URL;

const QuoteCalculator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { displayCurrency, formatCurrency } = useCurrency();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addOnServices, setAddOnServices] = useState([]);
  
  // Step 1: Organization Size
  const [orgSize, setOrgSize] = useState({
    users: 10,
    businessPlans: 5,
    subsidiaries: 2,
    scenarios: 10
  });
  
  // Step 2: Features Selection
  const [selectedFeatures, setSelectedFeatures] = useState({
    strategicFramework: true,
    businessIntelligence: true,
    advancedAnalytics: true,
    whiteLabel: false,
    apiAccess: false,
    customIntegrations: false,
    dedicatedSupport: true,
    dataStorage: 100, // GB
    exportCredits: 50
  });
  
  // Step 3: Contact Information
  const [contactInfo, setContactInfo] = useState({
    fullName: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    preferredContact: 'email',
    timeline: 'flexible'
  });

  useEffect(() => {
    fetchAddOnServices();
  }, []);

  const fetchAddOnServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/add-on-services`);
      setAddOnServices(response.data);
    } catch (error) {
      console.error('Error fetching add-on services:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate estimated price based on selections
  const calculateEstimate = () => {
    let basePrice = 100; // Enterprise base price
    
    // Capacity pricing
    const userPrice = Math.max(0, orgSize.users - 10) * 15; // $15 per user over 10
    const planPrice = Math.max(0, orgSize.businessPlans - 5) * 25; // $25 per plan over 5
    const subsidiaryPrice = orgSize.subsidiaries * 50; // $50 per subsidiary
    const scenarioPrice = Math.max(0, orgSize.scenarios - 10) * 10; // $10 per scenario over 10
    
    // Feature pricing
    const featurePrice = 
      (selectedFeatures.whiteLabel ? 500 : 0) +
      (selectedFeatures.apiAccess ? 100 : 0) +
      (selectedFeatures.customIntegrations ? 1500 : 0) +
      (selectedFeatures.dedicatedSupport ? 200 : 0) +
      (selectedFeatures.dataStorage > 50 ? (selectedFeatures.dataStorage - 50) * 0.5 : 0) +
      (selectedFeatures.exportCredits > 20 ? (selectedFeatures.exportCredits - 20) * 0.25 : 0);
    
    return basePrice + userPrice + planPrice + subsidiaryPrice + scenarioPrice + featurePrice;
  };

  const handleSubmitQuote = async () => {
    // Validate contact info
    if (!contactInfo.fullName || !contactInfo.email || !contactInfo.company) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const quoteData = {
        organization: orgSize,
        features: selectedFeatures,
        contact: contactInfo,
        estimatedPrice: calculateEstimate(),
        currency: displayCurrency,
        submittedAt: new Date().toISOString()
      };

      await axios.post(`${API}/api/quote-request`, quoteData);
      
      toast.success('Quote request submitted successfully! We\'ll be in touch within 24 hours.');
      navigate('/pricing');
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: 'Organization Size', icon: Building2 },
    { number: 2, title: 'Features & Add-ons', icon: Layers },
    { number: 3, title: 'Contact Details', icon: Mail },
    { number: 4, title: 'Review & Submit', icon: Send }
  ];

  const formatPrice = (usdPrice) => {
    return formatCurrency(usdPrice, 'USD', displayCurrency);
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isActive = currentStep === step.number;
        const isCompleted = currentStep > step.number;
        
        return (
          <React.Fragment key={step.number}>
            <div 
              className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                isActive 
                  ? 'bg-blue-600 border-blue-600 text-white' 
                  : isCompleted 
                    ? 'bg-green-500 border-green-500 text-white'
                    : 'bg-white border-gray-300 text-gray-400'
              }`}
            >
              {isCompleted ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
            </div>
            {index < steps.length - 1 && (
              <div className={`w-16 h-1 mx-2 ${
                currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-800">Organization Size</h2>
        <p className="text-slate-600 mt-2">Tell us about your organization&apos;s scale and requirements</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Users Slider */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-lg">Team Members</CardTitle>
            </div>
            <CardDescription>How many users need access?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-3xl font-bold text-blue-600">{orgSize.users}</span>
                <span className="text-sm text-slate-500">users</span>
              </div>
              <Slider
                value={[orgSize.users]}
                onValueChange={([value]) => setOrgSize(prev => ({ ...prev, users: value }))}
                min={1}
                max={100}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>1</span>
                <span>50</span>
                <span>100+</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Business Plans Slider */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-lg">Business Plans</CardTitle>
            </div>
            <CardDescription>Number of business plans needed</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-3xl font-bold text-purple-600">{orgSize.businessPlans}</span>
                <span className="text-sm text-slate-500">plans</span>
              </div>
              <Slider
                value={[orgSize.businessPlans]}
                onValueChange={([value]) => setOrgSize(prev => ({ ...prev, businessPlans: value }))}
                min={1}
                max={50}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>1</span>
                <span>25</span>
                <span>50+</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subsidiaries Slider */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-green-600" />
              <CardTitle className="text-lg">Subsidiaries</CardTitle>
            </div>
            <CardDescription>Number of subsidiaries or branches</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-3xl font-bold text-green-600">{orgSize.subsidiaries}</span>
                <span className="text-sm text-slate-500">subsidiaries</span>
              </div>
              <Slider
                value={[orgSize.subsidiaries]}
                onValueChange={([value]) => setOrgSize(prev => ({ ...prev, subsidiaries: value }))}
                min={0}
                max={20}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>0</span>
                <span>10</span>
                <span>20+</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scenarios Slider */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-orange-600" />
              <CardTitle className="text-lg">Scenarios</CardTitle>
            </div>
            <CardDescription>Number of scenario models needed</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-3xl font-bold text-orange-600">{orgSize.scenarios}</span>
                <span className="text-sm text-slate-500">scenarios</span>
              </div>
              <Slider
                value={[orgSize.scenarios]}
                onValueChange={([value]) => setOrgSize(prev => ({ ...prev, scenarios: value }))}
                min={1}
                max={50}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>1</span>
                <span>25</span>
                <span>50+</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-800">Features & Add-ons</h2>
        <p className="text-slate-600 mt-2">Select the features and add-ons you need</p>
      </div>

      {/* Core Enterprise Features (included) */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-yellow-500" />
            <CardTitle>Enterprise Core Features</CardTitle>
            <Badge className="bg-green-100 text-green-800 ml-2">Included</Badge>
          </div>
          <CardDescription>All enterprise features are included in your quote</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { name: 'Strategic Framework', icon: Target },
              { name: 'Business Intelligence', icon: BarChart3 },
              { name: 'Advanced Analytics', icon: Zap },
              { name: 'Cost Center Management', icon: Building2 },
              { name: 'Financial Management', icon: Briefcase },
              { name: 'Role Management', icon: Shield }
            ].map((feature) => (
              <div key={feature.name} className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                <Check className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-slate-700">{feature.name}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Premium Add-ons */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-purple-600" />
            <CardTitle>Premium Add-ons</CardTitle>
          </div>
          <CardDescription>Enhance your enterprise plan with additional capabilities</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* White Label */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Crown className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium">White-Label Branding</p>
                <p className="text-sm text-slate-500">Custom branding with your company logo</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-slate-600">{formatPrice(500)}/mo</span>
              <Switch
                checked={selectedFeatures.whiteLabel}
                onCheckedChange={(checked) => setSelectedFeatures(prev => ({ ...prev, whiteLabel: checked }))}
              />
            </div>
          </div>

          {/* API Access */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Settings className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium">API Access</p>
                <p className="text-sm text-slate-500">Full API access for integrations</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-slate-600">{formatPrice(100)}/mo</span>
              <Switch
                checked={selectedFeatures.apiAccess}
                onCheckedChange={(checked) => setSelectedFeatures(prev => ({ ...prev, apiAccess: checked }))}
              />
            </div>
          </div>

          {/* Custom Integrations */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Zap className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium">Custom Integrations</p>
                <p className="text-sm text-slate-500">Dedicated integration development</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-slate-600">{formatPrice(1500)}/mo</span>
              <Switch
                checked={selectedFeatures.customIntegrations}
                onCheckedChange={(checked) => setSelectedFeatures(prev => ({ ...prev, customIntegrations: checked }))}
              />
            </div>
          </div>

          {/* Dedicated Support */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Users className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="font-medium">Dedicated Support</p>
                <p className="text-sm text-slate-500">24/7 priority support with dedicated manager</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-slate-600">{formatPrice(200)}/mo</span>
              <Switch
                checked={selectedFeatures.dedicatedSupport}
                onCheckedChange={(checked) => setSelectedFeatures(prev => ({ ...prev, dedicatedSupport: checked }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage-based Options */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-600" />
            <CardTitle>Usage-Based Options</CardTitle>
          </div>
          <CardDescription>Adjust storage and export limits based on your needs</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Data Storage */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Data Storage</Label>
              <span className="text-lg font-bold text-blue-600">{selectedFeatures.dataStorage} GB</span>
            </div>
            <Slider
              value={[selectedFeatures.dataStorage]}
              onValueChange={([value]) => setSelectedFeatures(prev => ({ ...prev, dataStorage: value }))}
              min={10}
              max={500}
              step={10}
            />
            <div className="flex justify-between text-xs text-slate-400">
              <span>10 GB</span>
              <span>250 GB</span>
              <span>500 GB</span>
            </div>
          </div>

          {/* Export Credits */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Monthly Export Credits</Label>
              <span className="text-lg font-bold text-purple-600">{selectedFeatures.exportCredits}</span>
            </div>
            <Slider
              value={[selectedFeatures.exportCredits]}
              onValueChange={([value]) => setSelectedFeatures(prev => ({ ...prev, exportCredits: value }))}
              min={10}
              max={200}
              step={5}
            />
            <div className="flex justify-between text-xs text-slate-400">
              <span>10</span>
              <span>100</span>
              <span>200+</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-800">Contact Details</h2>
        <p className="text-slate-600 mt-2">Let us know how to reach you</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name *</Label>
              <Input
                id="fullName"
                placeholder="John Doe"
                value={contactInfo.fullName}
                onChange={(e) => setContactInfo(prev => ({ ...prev, fullName: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Work Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@company.com"
                value={contactInfo.email}
                onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">Company Name *</Label>
              <Input
                id="company"
                placeholder="Acme Inc."
                value={contactInfo.company}
                onChange={(e) => setContactInfo(prev => ({ ...prev, company: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="message">Additional Requirements or Questions</Label>
              <Textarea
                id="message"
                placeholder="Tell us about your specific needs, timeline, or any questions you have..."
                value={contactInfo.message}
                onChange={(e) => setContactInfo(prev => ({ ...prev, message: e.target.value }))}
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label>Preferred Contact Method</Label>
              <div className="flex gap-4">
                {['email', 'phone', 'video'].map((method) => (
                  <Button
                    key={method}
                    variant={contactInfo.preferredContact === method ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setContactInfo(prev => ({ ...prev, preferredContact: method }))}
                  >
                    {method.charAt(0).toUpperCase() + method.slice(1)}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Timeline</Label>
              <div className="flex gap-4">
                {[
                  { value: 'flexible', label: 'Flexible' },
                  { value: 'standard', label: '1-2 weeks' },
                  { value: 'urgent', label: 'ASAP' }
                ].map((timeline) => (
                  <Button
                    key={timeline.value}
                    variant={contactInfo.timeline === timeline.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setContactInfo(prev => ({ ...prev, timeline: timeline.value }))}
                  >
                    {timeline.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderStep4 = () => {
    const estimate = calculateEstimate();
    
    return (
      <div className="space-y-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-slate-800">Review Your Quote</h2>
          <p className="text-slate-600 mt-2">Confirm your selections before submitting</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle>Quote Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Organization Size */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2">Organization Size</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Users:</span>
                    <span className="font-medium">{orgSize.users}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Business Plans:</span>
                    <span className="font-medium">{orgSize.businessPlans}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Subsidiaries:</span>
                    <span className="font-medium">{orgSize.subsidiaries}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Scenarios:</span>
                    <span className="font-medium">{orgSize.scenarios}</span>
                  </div>
                </div>
              </div>

              <hr />

              {/* Selected Add-ons */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2">Selected Add-ons</h4>
                <div className="space-y-1 text-sm">
                  {selectedFeatures.whiteLabel && (
                    <div className="flex justify-between">
                      <span>White-Label Branding</span>
                      <span className="font-medium">{formatPrice(500)}/mo</span>
                    </div>
                  )}
                  {selectedFeatures.apiAccess && (
                    <div className="flex justify-between">
                      <span>API Access</span>
                      <span className="font-medium">{formatPrice(100)}/mo</span>
                    </div>
                  )}
                  {selectedFeatures.customIntegrations && (
                    <div className="flex justify-between">
                      <span>Custom Integrations</span>
                      <span className="font-medium">{formatPrice(1500)}/mo</span>
                    </div>
                  )}
                  {selectedFeatures.dedicatedSupport && (
                    <div className="flex justify-between">
                      <span>Dedicated Support</span>
                      <span className="font-medium">{formatPrice(200)}/mo</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Data Storage</span>
                    <span className="font-medium">{selectedFeatures.dataStorage} GB</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Export Credits</span>
                    <span className="font-medium">{selectedFeatures.exportCredits}/mo</span>
                  </div>
                </div>
              </div>

              <hr />

              {/* Contact Info */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2">Contact Information</h4>
                <div className="space-y-1 text-sm">
                  <p><span className="text-slate-500">Name:</span> {contactInfo.fullName}</p>
                  <p><span className="text-slate-500">Email:</span> {contactInfo.email}</p>
                  <p><span className="text-slate-500">Company:</span> {contactInfo.company}</p>
                  {contactInfo.phone && <p><span className="text-slate-500">Phone:</span> {contactInfo.phone}</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Estimate Card */}
          <Card className="bg-gradient-to-br from-blue-600 to-purple-600 text-white">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                Estimated Monthly Cost
              </CardTitle>
              <CardDescription className="text-blue-100">
                This is an estimate. Final pricing may vary based on specific requirements.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6">
                <div className="text-5xl font-bold mb-2">
                  {formatPrice(estimate)}
                </div>
                <p className="text-blue-100">per month</p>
                <div className="text-lg text-blue-100 mt-2">
                  ~{formatPrice(estimate * 12)}/year
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 mt-4">
                <p className="text-sm text-blue-100">
                  <strong>What happens next?</strong>
                </p>
                <ul className="text-sm text-blue-100 mt-2 space-y-1">
                  <li>• Our sales team will review your requirements</li>
                  <li>• You&apos;ll receive a detailed proposal within 24 hours</li>
                  <li>• We&apos;ll schedule a call to discuss your needs</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/pricing')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Pricing
          </Button>
          <h1 className="text-3xl font-bold text-slate-800">Enterprise Quote Calculator</h1>
          <p className="text-slate-600 mt-2">
            Get a custom quote tailored to your organization&apos;s needs
          </p>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Step Content */}
        <div className="mb-8">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Estimated Price Banner (shown on all steps) */}
        <Card className="mb-6 bg-slate-800 text-white">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calculator className="h-6 w-6 text-blue-400" />
                <div>
                  <p className="text-sm text-slate-300">Estimated Monthly Cost</p>
                  <p className="text-2xl font-bold">{formatPrice(calculateEstimate())}</p>
                </div>
              </div>
              <Badge className="bg-blue-500 text-white">
                Enterprise Plan
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
            disabled={currentStep === 1}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>

          {currentStep < 4 ? (
            <Button
              onClick={() => setCurrentStep(prev => Math.min(4, prev + 1))}
            >
              Next Step
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmitQuote}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Quote Request
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteCalculator;
